import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReportDataTable from './ReportDataTable';
import useSharedReportView from './useSharedReportView';
import { detailRequest, normalizeReportRows, reportDefinitions } from './reportDefinitions';
import './SharedReports.css';

const formatDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

async function fetchReport(request, signal) {
  const post = request.method === 'POST';
  const params = Object.fromEntries(Object.entries(request.params || {}).filter(([, value]) => value != null));
  const query = new URLSearchParams(params);
  const response = await fetch(`${request.endpoint}${post ? '' : `?${query}`}`, {
    signal, credentials: 'same-origin', method: post ? 'POST' : 'GET',
    ...(post ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(params) } : {}),
  });
  if (!response.ok) throw new Error(`Report could not be loaded (${response.status}). Please try again.`);
  const payload = await response.json();
  const rows = Array.isArray(payload) ? payload : payload?.data;
  if (payload?.success === false || !Array.isArray(rows)) throw new Error('The server did not return a valid report. Please try again.');
  return normalizeReportRows(request.key, rows);
}

function SavedReportTable({ reportKey, rows, onDetail }) {
  const definition = reportDefinitions[reportKey];
  const columns = useMemo(() => definition.columns.map(column => {
    const actionKey = reportKey === 'sales-customer-items' ? 'Invoices' : ['sales-items', 'purchase-items'].includes(reportKey) ? 'Details' : 'ItemSummary';
    if (onDetail && column.key === actionKey) return { ...column,
      render: row => <button type="button" className="shared-report-link" onClick={() => onDetail(row)}>
        {actionKey === 'ItemSummary' ? 'View Item Summary' : actionKey === 'Invoices' ? 'View Invoice' : 'View Details'}
      </button> };
    return column;
  }), [definition, onDetail, reportKey]);
  const defaults = useMemo(() => ({ version: 2, groupBy: definition.groupBy || [], expanded: [], columnWidths: {} }), [definition]);
  const preferences = useSharedReportView(reportKey, columns, defaults);
  return <ReportDataTable rows={rows} columns={columns} {...preferences} label={definition.title} initialSort={definition.initialSort} emptyLabel="No records match this report." />;
}

function ReportDialog({ level, index, active, onClose, children }) {
  const ref = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!active) return;
    const dialog = ref.current;
    dialog?.querySelector('.shared-dialog-close')?.focus({ preventScroll: true });
    const menu = () => document.querySelector('.summary-column-menu');
    const focusable = () => [...dialog.querySelectorAll('button, input, select, a[href], [tabindex]:not([tabindex="-1"])'),
      ...(menu()?.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])') || [])]
      .filter(el => !el.disabled && el.tabIndex >= 0 && el.getClientRects().length);
    const keydown = event => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        if (menu() || document.querySelector('.summary-is-dragging')) return;
        event.preventDefault(); close.current();
      }
      if (event.key === 'Tab') {
        const elements = focusable();
        if (!elements.length) { event.preventDefault(); dialog.focus(); return; }
        const i = elements.indexOf(document.activeElement);
        const next = i < 0 ? 0 : (i + (event.shiftKey ? -1 : 1) + elements.length) % elements.length;
        event.preventDefault(); elements[next].focus();
      }
    };
    const focusin = event => {
      if (!dialog.contains(event.target) && !menu()?.contains(event.target)) (focusable()[0] || dialog).focus();
    };
    document.addEventListener('keydown', keydown, true);
    document.addEventListener('focusin', focusin);
    return () => {
      document.removeEventListener('keydown', keydown, true);
      document.removeEventListener('focusin', focusin);
    };
  }, [active]);
  return createPortal(<div className="shared-report-overlay" style={{ zIndex: 1000 + index * 10 }} aria-hidden={!active || undefined}>
    <section ref={ref} className="shared-report-dialog" role="dialog" aria-modal={active ? 'true' : undefined} aria-labelledby={`report-dialog-${index}`} tabIndex={-1} {...(!active ? { inert: '' } : {})}>
      <header><div><h2 id={`report-dialog-${index}`}>{reportDefinitions[level.key].title}</h2><p title={level.subtitle}>{level.subtitle}</p></div>
        <button type="button" className="shared-dialog-close" onClick={onClose} aria-label="Close report">×</button></header>
      <div className="shared-dialog-content">{children}</div>
    </section>
  </div>, document.body);
}

export default function ReportPage({ reportKey }) {
  const definition = reportDefinitions[reportKey];
  const [fromDate, setFromDate] = useState(() => formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [toDate, setToDate] = useState(() => formatDate(new Date()));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [levels, setLevels] = useState([]);
  const requests = useRef(new Map());
  const dates = useRef(null);
  const pendingRestore = useRef(null);
  useEffect(() => () => { requests.current.forEach(controller => controller.abort()); }, []);
  useEffect(() => {
    if (!levels.length) return;
    const root = document.getElementById('root');
    const previous = document.body.style.overflow;
    const wasInert = root?.inert;
    document.body.style.overflow = 'hidden';
    if (root) root.inert = true;
    return () => { document.body.style.overflow = previous; if (root) root.inert = wasInert; };
  }, [levels.length > 0]);
  useEffect(() => {
    const trigger = pendingRestore.current;
    pendingRestore.current = null;
    if (trigger?.isConnected && !trigger.closest('[inert]')) trigger.focus({ preventScroll: true });
  }, [levels]);

  async function run(event) {
    event.preventDefault();
    if (!fromDate || !toDate || fromDate > toDate) { setError('Choose a valid date range. From date must not be after To date.'); return; }
    requests.current.get('main')?.abort();
    const controller = new AbortController(); requests.current.set('main', controller);
    setLoading(true); setError('');
    const range = { fromDate, toDate };
    try {
      const rows = await fetchReport({ key: reportKey, endpoint: definition.endpoint, params: { ...definition.params, ...range } }, controller.signal);
      if (!controller.signal.aborted) { dates.current = range; setResult(rows); }
    } catch (failure) { if (!controller.signal.aborted) setError(failure.message); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  }

  function closeLevel(index) {
    pendingRestore.current = levels[index]?.trigger;
    for (const [key, controller] of requests.current) if (typeof key === 'number' && key >= index) { controller.abort(); requests.current.delete(key); }
    setLevels(current => current.slice(0, index));
  }
  async function openDetail(parentKey, row, account, index) {
    const request = detailRequest(parentKey, row, dates.current, account);
    if (!request) return;
    requests.current.get(index)?.abort();
    const controller = new AbortController(); requests.current.set(index, controller);
    const level = { ...request, trigger: document.activeElement, loading: true, rows: [], error: '' };
    setLevels(current => [...current.slice(0, index), level]);
    try {
      const rows = await fetchReport(request, controller.signal);
      if (!controller.signal.aborted) setLevels(current => current.map((entry, i) => i === index ? { ...entry, rows, loading: false } : entry));
    } catch (failure) {
      if (!controller.signal.aborted) setLevels(current => current.map((entry, i) => i === index ? { ...entry, error: failure.message, loading: false } : entry));
    }
  }
  const supportsDetails = key => ['sales-customers', 'sales-customer-items', 'sales-items', 'purchase-items', 'purchase-suppliers'].includes(key);
  return <div className={`shared-report-page${result?.length ? ' is-report-ready' : ''}`}>
    {!result?.length && <>
      <header className="shared-report-heading"><span className="portal-eyebrow">{definition.section} / Reports</span><h1>{definition.title}</h1><p>Select a date range to view your report.</p></header>
      <form className="shared-report-dates" onSubmit={run}>
        <label htmlFor="sales-from-date">From date<input id="sales-from-date" type="date" value={fromDate} onChange={event => setFromDate(event.target.value)} required /></label>
        <label htmlFor="sales-to-date">To date<input id="sales-to-date" type="date" value={toDate} min={fromDate} onChange={event => setToDate(event.target.value)} required /></label>
        <button type="submit" disabled={loading}>{loading ? 'Loading…' : 'Run report'}<span aria-hidden="true">→</span></button>
      </form>
    </>}
    {error && <div role="alert" className="shared-report-error">{error}</div>}
    {loading && <div className="shared-report-loading" role="status">Loading report…</div>}
    {result !== null && <SavedReportTable key={reportKey} reportKey={reportKey} rows={result}
      onDetail={supportsDetails(reportKey) ? row => openDetail(reportKey, row, null, 0) : null} />}
    {levels.map((level, index) => <ReportDialog key={`${index}-${level.key}`} level={level} index={index} active={index === levels.length - 1} onClose={() => closeLevel(index)}>
      {level.loading ? <div className="shared-report-loading" role="status">Loading report…</div> : level.error ? <div className="shared-report-error" role="alert">{level.error}</div> :
        <SavedReportTable key={level.key} reportKey={level.key} rows={level.rows}
          onDetail={supportsDetails(level.key) ? row => openDetail(level.key, row, level.account, index + 1) : null} />}
    </ReportDialog>)}
  </div>;
}
